import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type ChangeEvent, useEffect, useRef } from 'react'

import type { ReactInputProps } from '../../types'
import { SCREENREADER_ONLY_STYLES } from '../../util/commonStyles'
import { CssVars, NeutralColorToken } from '../themes'
import { FoundationIcon } from './FoundationIcon'
import { InternalCheckboxOrRadioButtonRow } from './internal/InternalCheckboxOrRadioButtonRow'

export const testIds = {
  visibleCheckbox: 'rh-Checkbox-visibleCheckbox',
}

type BaseProps = {
  /**
   * `true` if the checkbox is checked, `false` if it's unchecked, or `"indeterminate"` if the checkbox
   * is in an indeterminate state.
   */
  isChecked: boolean | 'indeterminate'

  /**
   * The state of the checkbox. "disabled" marks it as disabled.
   */
  state?: 'default' | 'disabled' | false

  /**
   * Called when the user changes the value of the checkbox to either checked or unchecked. Note that
   * `checked` can only be `true` or `false`. `onChange` won't receive an "indeterminate" state.
   */
  onChange: ({
    event,
    isChecked,
  }: {
    event: ChangeEvent<HTMLInputElement>
    isChecked: boolean
  }) => void

  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

type InputProps = Omit<
  ReactInputProps,
  | keyof BaseProps
  | 'ref'
  | 'className'
  | 'style'
  | 'css'
  | 'type'
  | 'disabled'
  | 'checked'
>

type StandaloneCheckboxProps = BaseProps & InputProps

type Props = {
  /**
   * Label text shown next to the checkbox.
   *
   * Note that setting `aria-labelledby` may result in this label text not being associated
   * with the checkbox. If this is a problem for you, let us know in #bento-design-system-discussion.
   */
  label?: string
} & StandaloneCheckboxProps

const CHECKBOX_PIXEL_SIZE = 16

/**
 * Renders a single checkbox. If you would like to render a list of checkboxes, you may want to
 * use `CheckboxList` instead.
 */
export function Checkbox({
  label,
  overrideRootStyles,
  state = 'default',
  ...props
}: Props) {
  if (label) {
    return (
      <InternalCheckboxOrRadioButtonRow
        input={<StandaloneCheckbox {...props} state={state} />}
        inputHeight={CHECKBOX_PIXEL_SIZE}
        label={label}
        overrideRootStyles={overrideRootStyles}
      />
    )
  }
  return (
    <StandaloneCheckbox
      {...props}
      overrideRootStyles={overrideRootStyles}
      state={state}
    />
  )
}

function StandaloneCheckbox({
  isChecked,
  overrideRootStyles,
  state = 'default',
  ...props
}: StandaloneCheckboxProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = isChecked === 'indeterminate'
    }
  }, [isChecked])

  return (
    <div css={[{ display: 'inline-block' }, overrideRootStyles]}>
      <input
        {...props}
        ref={inputRef}
        checked={isChecked === true}
        // TODO(eric.lovett): Remove this class name once legacy input styles are removed
        // from web-app.
        className="remove-legacy"
        css={SCREENREADER_ONLY_STYLES}
        disabled={state === 'disabled'}
        onChange={event => {
          // NOTE(jare): Start by calling onChange with the event and the next
          // value of checked. We can ignore the "indeterminate" state here, since
          // the default change event doesn't switch "input.indeterminate" to true,
          // only to false.
          props.onChange?.({ event, isChecked: event.target.checked })

          // NOTE(jare): By default, the browser will flip "indeterminate" to
          // false when the value is changed. After we've passed the full event
          // to the parent via the onChange prop, we set input.indeterminate
          // back to match the state of the Checkbox component **before** we
          // rerender. Then, once we rerender, we'll set input.indeterminate
          // to match the state that the parent actually passes in.
          // TODO(jare): jsdom doesn't currently implement this behavior. If it
          // ever does or if we switch to something that doesn't use jsdom, add
          // a test for this.
          event.target.indeterminate = isChecked === 'indeterminate'
        }}
        type="checkbox"
      />
      <div
        // NOTE(jare): We hide this div from screen readers since it's only meant to
        // visually indicate the status of the checkbox
        aria-hidden="true"
        css={[
          {
            backgroundColor: CssVars.neutral.bg1,
            // NOTE(jare): Ideally we'd use focus-visible here, since that appears
            // to be the behavior for checkboxes across Chrome, Safari, and FF.
            // Currently the web-app applies the .no-focus-outline class to the
            // body if the user is using a pointing device to focus elements, so
            // we respect that classname here.
            // TODO(jare): Update this if we use decide to use :focus-visible or
            // switch to a polyfill
            'body:not(.no-focus-outline) input:focus + &': [
              {
                boxShadow: `
                  0 0 0 1px ${CssVars.focus.innerColor},
                  0 0 0 4px ${CssVars.focus.outerColor};
                `,
              },
              isChecked === false && { borderColor: CssVars.neutral.fg3 },
            ],
            borderColor: CssVars.neutral.fg2,
            borderRadius: 2,
            borderStyle: 'solid',
            borderWidth: 1,
            boxSizing: 'border-box',
            height: CHECKBOX_PIXEL_SIZE,
            width: CHECKBOX_PIXEL_SIZE,
          },
          isChecked === false
            ? {
                'input:active + &, &:active': {
                  borderColor: CssVars.neutral.fg3,
                },
                'input:disabled + &': {
                  backgroundColor: CssVars.neutral.bg2,
                  borderColor: CssVars.neutral.bg3,
                },
                'input:hover + &, &:hover': {
                  borderColor: CssVars.neutral.fg1,
                },
              }
            : {
                backgroundColor: CssVars.semantic.primary.base,
                border: 'none',
                'input:active + &, &:active': {
                  backgroundColor: CssVars.semantic.primary.pressed,
                },
                'input:disabled + &': {
                  backgroundColor: CssVars.neutral.fg3,
                },
                'input:hover + &, &:hover': {
                  backgroundColor: CssVars.semantic.primary.hover,
                },
              },
        ]}
        data-testid={testIds.visibleCheckbox}
        onClick={e => {
          // NOTE(jare): When this component is not the child of a label element, then
          // clicks on this div won't automatically be propagated to the input, so we
          // need to trigger them ourselves. If this component **is** rendered inside
          // of a label element, then clicks on this div will propagate up to the label
          // and trigger a click event on the input. We call preventDefault here to skip
          // over any native result of clicking this div and make sure we register a
          // single click on the input.
          e.preventDefault()

          // NOTE(jare): Forward any clicks on the presentational piece of
          // this component to the actual checkbox input and focus it after
          inputRef.current?.click()
          inputRef.current?.focus()
        }}
      >
        {isChecked !== false && (
          <div
            css={{
              display: 'flex',
              height: CHECKBOX_PIXEL_SIZE,
              width: CHECKBOX_PIXEL_SIZE,
            }}
          >
            <FoundationIcon
              color={NeutralColorToken.Bg1}
              type={isChecked === 'indeterminate' ? 'Minus16' : 'Checkmark16'}
            />
          </div>
        )}
      </div>
    </div>
  )
}
